import { useTranslation } from 'react-i18next';
import '../../../../styles/components/charts/chart.scss';
import { useEffect, useState } from 'react';
import LocationBasedConversionRatePieChart from './LocationBasedConversionRatePieChart';
import { RootState } from '../../../../store/store';
import get_graph_data_api_call from '../../../../lib/api/stats/get_graph_data_api_call';
import SingleCountryConversionRatePieChart from './SingleCountryConversionRatePieChart';
import { useSelector } from 'react-redux';
import ChartWithConfig from '../../ChartWithConfig';
import isNotEmptyString from '../../../../lib/helper-functions/isNotEmptyString';
import makeFirstLetterCapital from '../../../../lib/helper-functions/makeFirstLetterCapital';
import capitalizeFirstLetterOfEachWord from '../../../../lib/helper-functions/capitalizeFirstLetterOfEachWord';
import { defaultGraphTimeFilter } from '../../../../lib/constants/abstracts';

type Props = {
  timeFilter: string;
  customPeriodFilterDateRange: {
    start_date: string | undefined;
    end_date: string | undefined;
  };
  eventFilter: string;
};

const MainOverallAndLocationBasedCRChart = ({
  timeFilter = defaultGraphTimeFilter,
  customPeriodFilterDateRange,
  eventFilter = 'purchase',
}: Props) => {
  const { t } = useTranslation();
  const stats = useSelector((state: RootState) => state.user?.stats);
  const [params, setParams] = useState<string>(
    `?graph_type=conversion_rate&group_by=location&time_range=${timeFilter}&event_type=${eventFilter}`
  );
  const [response, setResponse] = useState<any>({ type: 'location', data: {} });
  const [isBackButtonVisible, setIsBackButtonVisible] =
    useState<boolean>(false);

  //Selected country
  const [selectedCountry, setSelectedCountry] = useState<string>('');
  const [title, setTitle] = useState<string>(t('OverallConversionRate'));
  const [comment, setComment] = useState<string>('');

  const renderCharts = () => {
    if (selectedCountry === '') {
      return (
        <LocationBasedConversionRatePieChart
          apiResponse={response}
          setSelectedCountry={setSelectedCountry}
        />
      );
    } else if (selectedCountry !== '') {
      return <SingleCountryConversionRatePieChart apiResponse={response} />;
    }
  };

  /* ---------API CALL----------- */
  const getGraphData = async () => {
    try {
      // Call to get conversion rate stats by location
      const res = await get_graph_data_api_call(
        stats.ai_tracker_api_key,
        stats.ai_tracker_access_token,
        params
      );

      if (res) {
        if (selectedCountry && selectedCountry !== '') {
          setResponse({ type: 'country', data: res });
        } else {
          setResponse({ type: 'location', data: res });
        }
      } else {
      }
    } catch (error) {
      console.error('Error fetching conversion rate data:', error);
    }
  };

  useEffect(() => {
    if (selectedCountry === '') {
      const title1 =
        eventFilter === 'purchase'
          ? t('LocationBasedConversionRate')
          : `${t('LocationBased')} ${eventFilter} ${t('ClickThroughRate')}`;
      setTitle(capitalizeFirstLetterOfEachWord(title1));

      const title2 =
        eventFilter === 'purchase'
          ? t('TheConversionRateOfCartsToCheckoutsBasedOnLocation')
          : `${t('LocationBased')} ${t('ClickThroughRate')} ${t(
              'of'
            )} ${eventFilter}`;

      setComment(capitalizeFirstLetterOfEachWord(title2));
    } else if (selectedCountry !== '') {
      const title1 = `${
        eventFilter === 'purchase'
          ? t('TheConversionRateOf')
          : t('ClickThroughRateOf')
      } ${selectedCountry}`;
      setTitle(capitalizeFirstLetterOfEachWord(title1));

      const title2 =
        eventFilter === 'purchase'
          ? `${t('ConversionRateCartsToCheckoutsIn')} ${selectedCountry}`
          : `${t('ClickThroughRate')} ${t('of')} ${eventFilter}`;

      setComment(capitalizeFirstLetterOfEachWord(title2));
    }
  }, [timeFilter, customPeriodFilterDateRange, selectedCountry, eventFilter]);

  useEffect(() => {
    let dateRangeURLParam = '';
    if (timeFilter === 'custom') {
      const { start_date, end_date } = customPeriodFilterDateRange || {};
      if (start_date === undefined || end_date === undefined) {
        return;
      }
      dateRangeURLParam = `&start_date=${start_date}&end_date=${end_date}`;
    }

    let locationURLParam = '';
    if (isNotEmptyString(selectedCountry)) {
      locationURLParam = `&group_by_value=${selectedCountry}`;
    }

    let eventURLParam = '';
    if (isNotEmptyString(eventFilter)) {
      eventURLParam = `&event_type=${eventFilter}`;
    }

    setParams(
      `?graph_type=conversion_rate&time_range=${timeFilter}${dateRangeURLParam}&group_by=location${locationURLParam}${eventURLParam}`
    );
  }, [timeFilter, customPeriodFilterDateRange, eventFilter, selectedCountry]);

  useEffect(() => {
    if (
      stats.ai_tracker_api_key !== '' &&
      stats.ai_tracker_access_token !== ''
    ) {
      setResponse({ type: 'load', data: [] });
      getGraphData();
    }
  }, [params, stats.ai_tracker_access_token, stats.ai_tracker_api_key]);

  const onBackButtonClick = () => {
    if (selectedCountry !== '') {
      setSelectedCountry('');
    }
  };

  useEffect(() => {
    if (selectedCountry !== '') {
      setIsBackButtonVisible(true);
    } else {
      setIsBackButtonVisible(false);
    }
  }, [selectedCountry]);

  return (
    <ChartWithConfig
      isBackButtonVisible={isBackButtonVisible}
      onBackButtonClick={onBackButtonClick}
      title={title}
      comment={comment}
    >
      {renderCharts()}
    </ChartWithConfig>
  );
};

export default MainOverallAndLocationBasedCRChart;
